/**
 * Sidebar navigation for the agent CRM interface.
 *
 * Navy background, dynamic agency branding, Lucide icons. The nav is grouped
 * into collapsible accordion sections (#541). On every load the nav opens
 * clean (#803): only the first group and the active route's group are
 * expanded, every other group collapsed -- no persisted "pile of open groups".
 * Collapsible on mobile (hidden by default, toggle via hamburger).
 *
 * #2579 (EPIC #2575 Tier 4) — DESKTOP ICON RAIL: a footer toggle collapses the
 * `lg:`+ sidebar from `w-64` to a `w-16` icon rail (persisted via the shared
 * `useSidebarCollapsed` hook). In rail mode group headers hide, each nav item
 * is icon-only with a CSS tooltip (`NavGroupSection collapsed` → `RailNavItem`),
 * the active-route accent survives, and the brand header shrinks to the logo
 * mark. Mobile keeps the existing off-canvas `translate-x` drawer at full width
 * UNTOUCHED — the rail width only applies at `lg:`.
 */
import { useLocation } from "react-router-dom";
import { usePermissions } from "../../contexts/auth";
import { X } from "lucide-react";
import { ThemeToggle } from "../ThemeToggle";
import { NavGroupSection } from "./NavGroupSection";
import { SidebarCollapseToggle } from "./SidebarCollapseToggle";
import { useBranding } from "../../hooks/useBranding";
import AgencyLogo from "../AgencyLogo";
import { useNavGroups } from "../../hooks/useNavGroups";
import { useSidebarCollapsed } from "../../hooks/useSidebarCollapsed";
import { useModules } from "../../contexts/modules";
import { useTour } from "../../contexts/tour";
import {
  navGroups,
  isItemVisible,
  isBackOfficePreset,
  worksFinanceDesk,
  worksParalegalDesk,
  type NavItem,
} from "./crmNav";

interface CrmSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CrmSidebar({ isOpen, onClose }: CrmSidebarProps) {
  const location = useLocation();
  const branding = useBranding();
  const { isEnabled } = useModules();
  const { has, preset, role, farmWideCanvass, hasOwnBook } = usePermissions();
  const { collapsed, toggleCollapsed } = useSidebarCollapsed();
  // Guided-tour orchestration (#1321): when a tour highlights a nav item the
  // engine asks for its accordion group to be expanded so the target is visible.
  const { navGroupToExpand } = useTour();

  // The back-office presets (#1178, + office_admin #1412, + finance_accounts
  // #1506). A back-office persona's data is NOT scoped to themselves, so their
  // own-book nav items (agentBookOnly) are permanently empty -- hide them. The
  // standard field agent (preset "none") and any CUSTOM preset keep the full
  // nav unchanged. (#1514) The preset set + the visibility predicate now live
  // in crmNav.ts so the command/navigation registry gates identically.
  // D2-USR-010 (#4001): a back-office user who still works their own book
  // (hasOwnBook) keeps those own-book items.
  const isBackOffice = isBackOfficePreset(preset);
  // #4157: the paralegal desk does not see the partner directory.
  const paralegalDesk = worksParalegalDesk(role, preset);
  // D4-FBK-4405: the finance desk never gets the own-book menu and loses the
  // deal-desk surfaces it does not work.
  const financeDesk = worksFinanceDesk(role, preset);

  const itemVisible = (item: NavItem) =>
    isItemVisible(item, {
      isEnabled,
      has,
      isBackOffice,
      farmWideCanvass,
      hasOwnBook,
      paralegalDesk,
      financeDesk,
    });

  // Build the visible groups (hide groups that end up empty after gating).
  const visibleGroups = navGroups
    .map((group) => ({ ...group, items: group.items.filter(itemVisible) }))
    .filter((group) => group.items.length > 0);

  const groupIds = visibleGroups.map((g) => g.id);

  const activeGroupId =
    visibleGroups.find((group) =>
      group.items.some((item) =>
        item.exact
          ? location.pathname === item.to
          : location.pathname.startsWith(item.to),
      ),
    )?.id ?? null;

  // Discoverability for managers (#2139): a manager
  // (view_management_dashboards) lands on /management (deal sign-off) and would
  // otherwise never find the read-only company dashboards in the Oversight
  // group, which defaults collapsed (#803). Open Oversight by default for them
  // only -- no other role gets an extra open group, and nothing about gating
  // changes. The group is only present in groupIds when its items survive
  // gating, so the extra id is a harmless no-op for anyone without the flag.
  const extraExpandedGroupIds = has("view_management_dashboards")
    ? ["oversight"]
    : [];

  // Clean baseline on every load (#803): only the first group and the active
  // route's group are expanded; everything else collapsed (no persisted pile).
  // Plus any caller-named discoverability groups (#2139, Oversight for managers).
  const { isExpanded, toggleGroup } = useNavGroups(
    groupIds,
    activeGroupId,
    extraExpandedGroupIds,
  );

  // #2579: the desktop rail width only applies at lg:. Below lg: the off-canvas
  // drawer stays full width regardless of the collapse preference.
  const widthClass = collapsed ? "w-64 lg:w-16" : "w-64";

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          role="presentation"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        data-collapsed={collapsed}
        className={`
          fixed top-0 left-0 z-50 h-screen ${widthClass} bg-navy flex flex-col
          transition-[transform,width] duration-200 ease-in-out motion-reduce:transition-none
          lg:translate-x-0 lg:z-auto
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Brand header — shrinks to the logo mark on the desktop rail. */}
        <div
          className={`flex items-center justify-between py-5 border-b border-navy-light ${
            collapsed ? "px-5 lg:px-0 lg:justify-center" : "px-5"
          }`}
        >
          <div
            className={`flex items-center gap-3 ${collapsed ? "lg:gap-0" : ""}`}
          >
            <AgencyLogo variant="compact" surface="dark" size="sm" />
            <span
              className={`text-lg font-semibold text-[#ffffff] ${
                collapsed ? "lg:hidden" : ""
              }`}
            >
              {branding.agency_name}
            </span>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2.5 -mr-1 rounded-lg hover:bg-navy-light transition-colors"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Nav groups */}
        <nav className={`flex-1 overflow-y-auto py-4 ${collapsed ? "px-2 lg:px-2" : "px-3"}`}>
          <ul className="space-y-1">
            {visibleGroups.map((group, index) => (
              <NavGroupSection
                key={group.id}
                id={group.id}
                label={group.label}
                icon={group.icon}
                items={group.items}
                expanded={isExpanded(group.id) || navGroupToExpand === group.id}
                onToggle={toggleGroup}
                onNavigate={onClose}
                collapsed={collapsed}
                first={index === 0}
              />
            ))}
          </ul>
        </nav>

        {/* Footer */}
        <div className="px-3 py-4 border-t border-navy-light">
          <div
            className={`flex items-center ${
              collapsed
                ? "justify-between lg:flex-col lg:gap-2"
                : "justify-between"
            }`}
          >
            <span
              className={`text-xs text-gray-500 px-2 ${
                collapsed ? "lg:hidden" : ""
              }`}
            >
              Agent CRM
            </span>
            <div className={`flex items-center gap-1 ${collapsed ? "lg:flex-col" : ""}`}>
              <ThemeToggle />
              {/* Desktop-only rail toggle. */}
              <SidebarCollapseToggle
                collapsed={collapsed}
                onToggle={toggleCollapsed}
              />
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
